import { useEffect, useState } from "react";
import { cn } from "@/utils/cn";
import { minorToInput, parseMoneyMinor } from "@/utils/currency";

/** Finance target interface, plus optional `invalid`, `autoFocus`, `ariaLabel` and `required` (target: absent). */
export interface MoneyInputProps {
  valueMinor: number | null;
  currency: string;
  onChange: (v: number | null) => void;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  ariaLabel?: string;
  /** Marks the input `aria-required`, beside the star in its label. */
  required?: boolean;
  className?: string;
}

/**
 * Text input for money in minor units. Keeps the raw text while focused so a
 * half-typed "12," is not reformatted under the cursor; normalises on blur.
 */
export function MoneyInput({ valueMinor, currency, onChange, disabled = false, invalid = false, autoFocus = false, ariaLabel, required = false, className }: MoneyInputProps) {
  const [text, setText] = useState(minorToInput(valueMinor));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(minorToInput(valueMinor));
  }, [valueMinor, focused]);

  return (
    <div className={cn("relative", className)}>
      <input
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        autoFocus={autoFocus}
        disabled={disabled}
        value={text}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setText(minorToInput(parseMoneyMinor(text)));
        }}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parseMoneyMinor(e.target.value));
        }}
        className={cn(
          "w-full h-9 pl-3 pr-12 rounded-lg border bg-white text-sm text-right tabular-nums",
          "focus:outline-hidden focus:ring-2 focus:ring-blue-500/30",
          "disabled:bg-gray-50 disabled:text-gray-500",
          invalid ? "border-red-400" : "border-gray-200 focus:border-blue-400"
        )}
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{currency}</span>
    </div>
  );
}
